// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:5013',
  TENANT_KEY: import.meta.env.VITE_TENANT_KEY || 'default-tenant',
  API_PREFIX: '/csa-srv',
  TIMEOUT: 30000, // 30 seconds
};

// API Endpoints
export const API_ENDPOINTS = {
  // Auth/Customers
  AUTH: {
    LOGIN: '/customers/auth/phone',
    GENERATE_OTP: '/customers/otp/generate',
    VERIFY_OTP: '/customers/otp/verify',
    COMPLETE_REGISTRATION: '/customers/complete-registration',
    LOGOUT: '/customers/auth/logout',
    ME: '/customers/me',
  },
  
  // Products
  PRODUCTS: {
    BASE: '/products',
    BY_ID: (id: string) => `/products/${id}`,
    BY_SKU: (sku: string) => `/products/sku/${sku}`,
    BY_BARCODE: (barcode: string) => `/products/barcode/${barcode}`,
    CATEGORIES: '/products/categories',
    BRANDS: '/products/brands',
    LOW_STOCK: '/products/low-stock',
  },
  
  // Categories
  CATEGORIES: {
    BASE: '/categories',
    BY_ID: (id: string) => `/categories/${id}`,
    BY_SLUG: (slug: string) => `/categories/slug/${slug}`,
    TREE: '/categories/tree',
    CHILDREN: (id: string) => `/categories/${id}/children`,
  },
  
  // Brands
  BRANDS: {
    BASE: '/brands',
    BY_ID: (id: string) => `/brands/${id}`,
    BY_SLUG: (slug: string) => `/brands/slug/${slug}`,
  },
  
  // Orders
  ORDERS: {
    BASE: '/orders',
    BY_ID: (id: string) => `/orders/${id}`,
    BY_NUMBER: (orderNumber: string) => `/orders/number/${orderNumber}`,
    CREATE: '/orders',
    // JWT-based endpoints (no customer ID needed)
    MY_ORDERS: '/orders/my-orders',
    CREATE_MY_ORDER: '/orders/my-order',
    CHECK_TRANSACTION_ID: (transactionId: string) => `/orders/check-transaction-id/${transactionId}`,
    // Admin endpoints (keep for backward compatibility)
    CUSTOMER_ORDERS: (customerId: string) => `/orders/customer/${customerId}`,
    STATUS: (id: string) => `/orders/${id}/status`,
    CANCEL: (id: string) => `/orders/${id}/cancel`,
    STATISTICS: '/orders/statistics',
    STATUS_HISTORY: (id: string) => `/orders/${id}/status-history`,
  },
  
  // Customers
  CUSTOMERS: {
    BASE: '/customers',
    BY_ID: (id: string) => `/customers/${id}`,
    BY_PHONE: (phone: string) => `/customers/phone/${phone}`,
    REGISTER: '/customers/register',
    // JWT-based endpoints (no customer ID needed)
    ME: '/customers/me',
    UPDATE_ME: '/customers/me',
    MY_ADDRESSES: '/customers/me/shipping-addresses',
    ADD_MY_ADDRESS: '/customers/me/shipping-addresses',
    DELETE_MY_ADDRESS: (addressId: string) => `/customers/me/shipping-addresses/${addressId}`,
    MY_WISHLIST: '/customers/me/wishlist',
    // Admin endpoints (keep for backward compatibility)
    UPDATE: (id: string) => `/customers/${id}`,
    ADDRESSES: (id: string) => `/customers/${id}/addresses`,
    ADD_ADDRESS: (id: string) => `/customers/${id}/addresses`,
    UPDATE_ADDRESS: (customerId: string, addressId: string) => `/customers/${customerId}/addresses/${addressId}`,
    DELETE_ADDRESS: (customerId: string, addressId: string) => `/customers/${customerId}/addresses/${addressId}`,
    SET_DEFAULT_ADDRESS: (customerId: string, addressId: string) => `/customers/${customerId}/addresses/${addressId}/set-default`,
    WISHLIST: (id: string) => `/customers/${id}/wishlist`,
    ADD_WISHLIST: (id: string) => `/customers/${id}/wishlist`,
    REMOVE_WISHLIST: (customerId: string, productId: string) => `/customers/${customerId}/wishlist/${productId}`,
  },
  
  // Abandoned Cart
  ABANDONED_CART: {
    BASE: '/abandoned-cart',
    // JWT-based endpoints (no customer ID needed)
    MY_CART: '/abandoned-cart/my-cart',
    UPDATE_MY_CART: '/abandoned-cart/my-cart',
    CLEAR_MY_CART: '/abandoned-cart/my-cart',
    // Admin endpoints (keep for backward compatibility)
    BY_CUSTOMER: (customerId: string) => `/abandoned-cart/customer/${customerId}`,
    UPDATE: (customerId: string) => `/abandoned-cart/customer/${customerId}`,
    CLEAR: (customerId: string) => `/abandoned-cart/customer/${customerId}`,
  },
  
  // Company
  COMPANY: {
    BASE: '/company',
    PUBLIC: '/company/public',
    PICKUP_LOCATIONS: '/company/pickup-locations',
  },
  
  // Flash News
  FLASH_NEWS: {
    BASE: '/flashnews',
    ACTIVE: '/flashnews/active',
    DISMISS: (id: string) => `/flashnews/${id}/dismiss`,
  },
  
  // Settings
  SETTINGS: {
    BASE: '/settings',
    PUBLIC: '/settings/public',
    BY_KEY: (key: string) => `/settings/key/${key}`,
  },
  
  // Coupons
  COUPONS: {
    VALIDATE: '/coupons/validate',
    APPLY: '/coupons/apply',
  },
  
  // Payment Extraction
  PAYMENT_EXTRACTION: {
    EXTRACT: '/payment-extraction/extract-customer', // Customer endpoint
    EXTRACT_ADMIN: '/payment-extraction/extract', // Admin endpoint
    VALIDATE_TOTAL: '/payment-extraction/validate-total',
  },
  
  // Tags
  TAGS: {
    BASE: '/tags',
    BY_ID: (id: string) => `/tags/${id}`,
  },
  
  // Upload
  UPLOAD: {
    PAYMENT_PROOF: '/upload/payment-proof',
    PRODUCT_IMAGE: '/upload/product-image',
  },
};

// Helper function to build full URL
export const buildApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${API_CONFIG.API_PREFIX}${endpoint}`;
};